// Client side firebase

import { getApp, getApps, initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import type { Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import { env, shouldUseFirebaseEmulators } from './environment';
import { logDebugInfo } from './debug';
import { getFirebaseClientConfig } from './firebase-client-config';

// Helper functions to safely check emulator connection status
function isAuthEmulatorConnected(auth: Auth): boolean {
  try {
    // Check if auth emulator is already connected
    return !!(auth as unknown as { _delegate?: { _config?: { emulator?: unknown } } })._delegate?._config?.emulator;
  } catch {
    return false;
  }
}

function isFirestoreEmulatorConnected(db: Firestore): boolean {
  try {
    // Check if firestore emulator is already connected
    const settings = (db as unknown as { _delegate?: { _databaseId?: { projectId?: string; host?: string } } })
      ._delegate?._databaseId;
    return settings?.projectId?.includes('localhost') || settings?.host?.includes('localhost');
  } catch {
    return false;
  }
}

// Lazy-loaded Firebase instances to avoid client-side hydration issues
let app: ReturnType<typeof getApp> | undefined;
let auth: ReturnType<typeof getAuth> | undefined;
let db: ReturnType<typeof getFirestore> | undefined;
let firebaseConfig: Awaited<ReturnType<typeof getFirebaseClientConfig>> | null = null;
let initializationPromise: Promise<void> | null = null;

async function initializeFirebase() {
  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    try {
      // Only get config when actually needed
      if (!firebaseConfig) {
        firebaseConfig = await getFirebaseClientConfig();
      }

      // Initialize Firebase app
      app = getApps().length ? getApp() : initializeApp(firebaseConfig);
      auth = getAuth(app);
      db = getFirestore(app);

      // Connect to Firebase emulators in development
      if (shouldUseFirebaseEmulators() && typeof window !== 'undefined') {
        // Use dynamic imports to connect to emulators
        const [{ connectAuthEmulator }, { connectFirestoreEmulator }] = await Promise.all([
          import('firebase/auth'),
          import('firebase/firestore'),
        ]);

        try {
          // Connect to Auth Emulator - safer check
          if (auth && !isAuthEmulatorConnected(auth)) {
            connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
            console.log('🔥 Connected to Auth Emulator at http://127.0.0.1:9099');
          }
        } catch (error) {
          console.warn('Auth Emulator connection failed:', error);
        }

        try {
          // Connect to Firestore Emulator - safer check
          if (db && !isFirestoreEmulatorConnected(db)) {
            connectFirestoreEmulator(db, '127.0.0.1', 8080);
            console.log('🔥 Connected to Firestore Emulator at 127.0.0.1:8080');
          }
        } catch (error) {
          console.warn('Firestore Emulator connection failed:', error);
        }
      }

      if (env.isDebugMode) {
        console.log('🔥 Firebase initialized:', {
          projectId: firebaseConfig.projectId,
          environment: env.environment,
          usingEmulators: shouldUseFirebaseEmulators(),
        });

        // Log detailed debug information
        logDebugInfo();
      }
    } catch (error) {
      console.error('Failed to initialize Firebase', error);
      throw error;
    }
  })();

  return initializationPromise;
}

// Helper functions to get Firebase instances with lazy initialization
async function getFirebaseApp() {
  if (!app) {
    await initializeFirebase();
  }
  return app;
}

async function getFirebaseAuth() {
  if (!auth) {
    await initializeFirebase();
  }
  return auth;
}

async function getFirebaseDb() {
  if (!db) {
    await initializeFirebase();
  }
  return db;
}

const ORDER_COLLECTION = 'jayouz/v1/orders' as const;
const MESSAGE_COLLECTION = 'jayouz/v1/messages' as const;
const PRODUCT_COLLECTION = 'jayouz/v1/products' as const;
const PRODUCTS_AGGREGATE_COLLECTION = 'jayouz/v1/products_aggregate' as const;
const PRODUCT_VARIANTS_COLLECTION = 'jayouz/v1/product_variants' as const;
const ORDERS_AGGREGATE_COLLECTION = 'jayouz/v1/orders_aggregate' as const;
const STRIPE_PAYMENT_LINKS = 'jayouz/v1/stripe_payment_links' as const;
const PAYPAL_PAYMENT_LINKS = 'jayouz/v1/paypal_payment_links' as const;
const PROCESSED_STRIPE_EVENTS = 'jayouz/v1/processed_stripe_events';

// Initialize Firebase with clean async architecture
// No synchronous initialization to avoid environment variable issues

export {
  app,
  auth,
  db,
  getFirebaseApp,
  getFirebaseAuth,
  getFirebaseDb,
  initializeFirebase,
  ORDER_COLLECTION,
  MESSAGE_COLLECTION,
  PRODUCTS_AGGREGATE_COLLECTION,
  PRODUCT_COLLECTION,
  PRODUCT_VARIANTS_COLLECTION,
  ORDERS_AGGREGATE_COLLECTION,
  STRIPE_PAYMENT_LINKS,
  PAYPAL_PAYMENT_LINKS,
  PROCESSED_STRIPE_EVENTS,
};
